import React from 'react';

import {
  useSelector,
} from 'react-redux';

import Accordion
  from 'react-bootstrap/Accordion';

import Container
  from 'react-bootstrap/Container';

import {
  Link,
} from 'react-router-dom';

import {
  getItemsStatus,
  getItemsError,
  selectMainHomes,
} from './homesSlice';

import {
  useLanguage,
} from '../../context/LanguageContext';

import SingleSlide
  from '../../components/contentElements/SingleSlide';

import Blockquote
  from '../../components/contentElements/Blockquote';

import './HomesMainList.css';


const HomesMainList = () => {
  const {
    language,
  } = useLanguage();

  const homes =
    useSelector(
      selectMainHomes
    );

  const itemStatus =
    useSelector(
      getItemsStatus
    );

  const error =
    useSelector(
      getItemsError
    );


  const getTitle = (home) => {
    if (language === 'fr') {
      return (
        home.title_2026_fr ||
        home.title_2026_en ||
        ''
      );
    }

    return (
      home.title_2026_en ||
      home.title_2026_fr ||
      ''
    );
  };


  const getContent = (home) => {
    if (language === 'fr') {
      return (
        home.content_2026_fr ||
        home.content_2026_en ||
        ''
      );
    }

    return (
      home.content_2026_en ||
      home.content_2026_fr ||
      ''
    );
  };


  /*
   * Only 2026 programme records are
   * displayed on the current website.
   */
  const currentHomes =
    homes.filter(
      (home) =>
        home.title_2026_en ||
        home.title_2026_fr ||
        home.content_2026_en ||
        home.content_2026_fr
    );


  /*
   * Group current programme records
   * by strategic pillar.
   */
  const homesByPilar =
    currentHomes.reduce(
      (
        accumulator,
        home
      ) => {
        if (
          home.pilar_id
        ) {
          if (
            !accumulator[
              home.pilar_id
            ]
          ) {
            accumulator[
              home.pilar_id
            ] = [];
          }

          accumulator[
            home.pilar_id
          ].push(home);
        }

        return accumulator;
      },
      {}
    );


  Object
    .values(
      homesByPilar
    )
    .forEach(
      (pillarHomes) => {
        pillarHomes.sort(
          (a, b) =>
            a.id - b.id
        );
      }
    );


  if (
    itemStatus === 'loading'
  ) {
    return (
      <>
        <SingleSlide />

        <Blockquote />

        <Container className="programme-loading">
          {language === 'fr'
            ? 'Chargement du programme...'
            : 'Loading programme...'}
        </Container>
      </>
    );
  }


  if (
    itemStatus === 'failed'
  ) {
    return (
      <>
        <SingleSlide />

        <Blockquote />

        <Container className="programme-loading">
          <p>
            {language === 'fr'
              ? 'Impossible de charger le programme.'
              : 'Unable to load programme content.'}
          </p>

          {error && (
            <small>
              {String(error)}
            </small>
          )}
        </Container>
      </>
    );
  }


  const renderedPillars =
    Object
      .entries(
        homesByPilar
      )
      .sort(
        (
          [pillarA],
          [pillarB]
        ) =>
          Number(
            pillarA
          ) -
          Number(
            pillarB
          )
      )
      .map(
        (
          [
            pilarId,
            pillarHomes,
          ]
        ) => {
          const mainHome =
            pillarHomes.find(
              (home) =>
                home.main === true
            );

          const detailHomes =
            pillarHomes.filter(
              (home) =>
                home.main !== true
            );

          if (!mainHome) {
            return null;
          }

          return (
            <Accordion
              key={
                `programme-pillar-${pilarId}`
              }
              className="programme-accordion"
              flush
            >
              <Accordion.Item
                eventKey="0"
                className="programme-accordion-item"
              >
                <Accordion.Header>
                  <div className="programme-header">
                    <div className="programme-header-text">
                      <span className="programme-label">
                        {language === 'fr'
                          ? `Pilier ${pilarId} : `
                          : `Pillar ${pilarId}: `}

                        {getTitle(
                          mainHome
                        )}
                      </span>
                    </div>
                  </div>
                </Accordion.Header>

                <Accordion.Body>
                  <div className="programme-body">
                    {detailHomes.map(
                      (home) => (
                        <section
                          key={
                            `programme-content-${home.id}`
                          }
                          className="programme-section"
                        >
                          <div className="programme-section-content">
                            <h3>
                              {getTitle(
                                home
                              )}
                            </h3>

                            <div className="programme-small-line" />

                            <p>
                              {getContent(
                                home
                              )}
                            </p>
                          </div>
                        </section>
                      )
                    )}

                    <div className="programme-framework">
                      <div>
                        <span className="programme-framework-label">
                          {language === 'fr'
                            ? 'Suivi des résultats'
                            : 'Measuring results'}
                        </span>

                        <h3>
                          {language === 'fr'
                            ? 'Cadre de résultats 2026–2030'
                            : 'Results Framework 2026–2030'}
                        </h3>

                        <p>
                          {language === 'fr'
                            ? 'Consultez les résultats, produits et indicateurs utilisés pour mesurer les progrès de ce pilier.'
                            : 'Explore the outcomes, outputs and indicators used to measure progress under this pillar.'}
                        </p>
                      </div>

                      <Link
                        to={
                          `/results-framework/pillar/${pilarId}`
                        }
                        className="programme-framework-link"
                      >
                        {language === 'fr'
                          ? 'Voir le cadre de résultats'
                          : 'View Results Framework'}

                        <span
                          aria-hidden="true"
                        >
                          →
                        </span>
                      </Link>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          );
        }
      );


  return (
    <>
      <SingleSlide />

      <Blockquote />

      <main className="programme-page">
        <Container>
          <header className="programme-introduction">
            <span className="programme-introduction-label">
              {language === 'fr'
                ? 'Programme ACE 2026–2030'
                : 'ACE Programme 2026–2030'}
            </span>

            <h1>
              {language === 'fr'
                ? 'Nos trois piliers stratégiques'
                : 'Our Three Strategic Pillars'}
            </h1>

            <div className="programme-title-line" />

            <p>
              {language === 'fr'
                ? "Découvrez chaque pilier du programme, sa théorie du changement, son concept, ses résultats attendus et son alignement sur les Objectifs de développement durable."
                : 'Explore each programme pillar, its theory of change, concept, expected outcomes and alignment with the Sustainable Development Goals.'}
            </p>
          </header>

          <div className="programme-list">
            {renderedPillars}
          </div>
        </Container>
      </main>
    </>
  );
};


export default HomesMainList;
